import React, { useState, useEffect, useCallback, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config.js";
import RoleAdd from "./RoleAdd";
import SubTitleNew from "../common/New/SubTitleNew.jsx";
import DeleteModal from "../common/DeleteModal";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress } from "@mui/material";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import StatsCard from "../users/StatsCard.jsx";
import DynamicTable from "../common/New/DynamicTable.jsx";
import { TableStatusBadge } from "../common/New/DynamicTable.jsx";
import { useUser } from "../../pages/login/UserContextPersistent";
import Update_Icon from "../../images/table/Update_new.svg";
import View_Icon from "../../images/table/View_icon.svg";
import Delete_Icon from "../../images/table/delete_new.svg";
import {
  downloadBackendExcel,
  exportTableToCSV,
} from "../../utils/exportTableData";
import "../users/UsersMain.css";

async function getAllRoles() {
  try {
    const response = await axios.get(`${config.baseURL}/role/organization/`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
}

async function createRole(role) {
  try {
    const response = await axios.post(`${config.baseURL}/role`, role, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating role:", error);
    throw error;
  }
}

async function updateRole(role) {
  try {
    const response = await axios.put(`${config.baseURL}/role`, role, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating role:", error);
    throw error;
  }
}

async function deleteRole(id) {
  try {
    const response = await axios.delete(`${config.baseURL}/role/${id}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting role:", error);
    throw error;
  }
}

function getFeatureName(feature) {
  if (feature?.feature?.name) return feature.feature.name;
  if (typeof feature === "object" && feature?.name) return feature.name;
  return feature;
}

function formatAccessFeatures(features) {
  if (!Array.isArray(features) || features.length === 0) {
    return "No access assigned";
  }

  const names = features.map(getFeatureName).filter(Boolean);
  const displayFeatures = names.slice(0, 3);
  let displayText = displayFeatures.join(", ");
  if (names.length > 3) {
    displayText += ` +${names.length - 3} more`;
  }
  return displayText;
}

function getRoleStatus(role) {
  const raw = role?.status ?? role?.roleStatus;
  if (raw != null && raw !== "") {
    const value = String(raw).toUpperCase();
    if (
      value.includes("INACTIV") ||
      value === "DISABLED" ||
      value === "FALSE" ||
      value === "0"
    ) {
      return "INACTIVE";
    }
    return "ACTIVE";
  }
  if (role?.active === false || role?.isActive === false || role?.enabled === false) {
    return "INACTIVE";
  }
  return "ACTIVE";
}

export default function RoleMain() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { organizationInactive } = useUser();
  const [roleList, setRoleList] = useState([]);
  const [userCounts, setUserCounts] = useState({});
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [expandedRoleId, setExpandedRoleId] = useState(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [visibleRoleCount, setVisibleRoleCount] = useState(10);
  const [expandedMode, setExpandedMode] = useState("edit");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const isSchoolApp =
    localStorage.getItem("selectedApp") === "schoolTrack" ||
    location.pathname.startsWith("/school-users");
  const usersListPath = isSchoolApp ? "/school-users" : "/user";
  const canCreateRole = !organizationInactive;
  const canUpdateRole = !organizationInactive;
  const canDeleteRole = !organizationInactive;
  const canViewRole = true;
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  const getAllTheAvailableFeatures = useCallback(async () => {
    const response = await axios.get(`${config.baseURL}/feature/`, {
      withCredentials: true,
    });
    return response.data;
  }, []);

  const assignFeatureToTheRole = useCallback(async (roleId, featureIds) => {
    setIsAssigning(true);
    try {
      const response = await axios.post(
        `${config.baseURL}/role/feature`,
        {
          roleId,
          featureId: featureIds,
        },
        { withCredentials: true },
      );
      return response.data;
    } finally {
      setIsAssigning(false);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAllRoles();
      setRoleList(Array.isArray(response?.roles) ? response.roles : []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      if (error.response?.status === 401) {
        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const fetchUserCounts = useCallback(async () => {
    try {
      const userState = JSON.parse(localStorage.getItem("userState"));
      const organizationId = userState?.user?.organizationId;
      if (!organizationId) return;

      const response = await axios.get(
        `${config.baseURL}/user/organization/${organizationId}`,
        { withCredentials: true },
      );
      const users = Array.isArray(response.data?.users) ? response.data.users : [];
      const counts = {};
      users.forEach((user) => {
        const roleId = user.roles?.id || user.roleId || user.role?.id;
        if (roleId) {
          counts[roleId] = (counts[roleId] || 0) + 1;
        }
      });
      setUserCounts(counts);
    } catch (error) {
      console.error("Error fetching user counts for roles:", error);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchUserCounts();
  }, [fetchRoles, fetchUserCounts]);

  const tableRows = roleList.map((role) => {
    const status = getRoleStatus(role);
    return {
      ...role,
      roleName: role.name,
      permissions: formatAccessFeatures(role.features),
      usersCount: userCounts[role.id] || 0,
      status,
    };
  });

  const filteredRoles = tableRows.filter((role) => {
    if (statusFilter && role.status !== statusFilter) {
      return false;
    }
    if (!searchText) {
      return true;
    }
    const query = searchText.toLowerCase();
    return (
      (role.roleName && role.roleName.toLowerCase().includes(query)) ||
      (role.description && role.description.toLowerCase().includes(query)) ||
      (role.permissions && role.permissions.toLowerCase().includes(query))
    );
  });

  const visibleRoles = filteredRoles.slice(0, visibleRoleCount);
  const hasMoreRoles = filteredRoles.length > visibleRoleCount;

  useEffect(() => {
    setVisibleRoleCount(10);
  }, [searchText, statusFilter]);

  const activeRolesCount = tableRows.filter((role) => role.status === "ACTIVE")
    .length;

  const closeForms = () => {
    setShowCreateRole(false);
    setExpandedRoleId(null);
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    setExpandedRoleId(null);
    setVisibleRoleCount(10);
  };

  const handleCreateRole = () => {
    if (!canCreateRole) return;
    setExpandedRoleId(null);
    setShowCreateRole((open) => !open);
  };

  const handleRoleFormComplete = async (savedRole, { created } = {}) => {
    closeForms();
    await fetchRoles();
    await fetchUserCounts();
    if (created && savedRole?.id) {
      setExpandedRoleId(savedRole.id);
      setExpandedMode("edit");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete || !canDeleteRole) return;

    try {
      await deleteRole(roleToDelete.id);
      setRoleList((prev) => prev.filter((role) => role.id !== roleToDelete.id));
    } catch (error) {
      console.error("Error deleting role:", error);
    } finally {
      setIsDeleteModalOpen(false);
      setRoleToDelete(null);
    }
  };

  const toggleExpanded = (roleId, mode) => {
    setShowCreateRole(false);
    setExpandedMode(mode);
    setExpandedRoleId((current) =>
      String(current) === String(roleId) && expandedMode === mode ? null : roleId,
    );
  };

  const handleActionClick = (actionId, role) => {
    switch (actionId) {
      case "edit":
        if (canUpdateRole) {
          toggleExpanded(role.id, "edit");
        }
        break;
      case "view":
        if (canViewRole) {
          toggleExpanded(role.id, "view");
        }
        break;
      case "delete":
        if (canDeleteRole) {
          setRoleToDelete(role);
          setIsDeleteModalOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const displayHeaders = [
    {
      key: "roleName",
      label: "Role",
      width: "16%",
      render: (row) => <strong>{row.roleName}</strong>,
    },
    {
      key: "description",
      label: "Description",
      width: "28%",
      render: (row) => row.description || "—",
    },
    {
      key: "permissions",
      label: "Permissions",
      width: "28%",
      render: (row) => row.permissions,
    },
    {
      key: "usersCount",
      label: "Users",
      width: "8%",
      render: (row) => row.usersCount,
    },
    // {
    //   key: "status",
    //   label: "Status",
    //   width: "10%",
    //   render: (row) => (
    //     <TableStatusBadge tone={row.status === "ACTIVE" ? "green" : "gray"}>
    //       {t(row.status === "ACTIVE" ? "Active" : "Inactive")}
    //     </TableStatusBadge>
    //   ),
    // },
  ];

  const displayActions = [
    canUpdateRole && {
      id: "edit",
      icon: Update_Icon,
      label: t("Update"),
      onClick: (row) => handleActionClick("edit", row),
    },
    canViewRole && {
      id: "view",
      icon: View_Icon,
      label: t("View"),
      onClick: (row) => handleActionClick("view", row),
    },
    canDeleteRole && {
      id: "delete",
      icon: Delete_Icon,
      label: t("Delete"),
      onClick: (row) => handleActionClick("delete", row),
    },
  ].filter(Boolean);

  const expandedRole = roleList.find(
    (role) => String(role.id) === String(expandedRoleId),
  );

  const userState = localStorage.getItem("userState");
  const parsedUserState = userState ? JSON.parse(userState) : null;
  const cleanOrgName = (
    parsedUserState?.organization?.name ||
    orgName ||
    "RouteEye"
  ).replace(/[^a-zA-Z0-9_-]/g, "_");
  const roleExportFileName = `${cleanOrgName}_${isSchoolApp ? "School_Roles" : "Roles"}`;

  const handleDownloadRoles = async () => {
    const downloadFileName = `${roleExportFileName}.xlsx`;
    const targetRoles = (selectedRoleIds && selectedRoleIds.length > 0)
      ? filteredRoles.filter((r) => selectedRoleIds.some((id) => String(id) === String(r.id)))
      : filteredRoles;

    const exportRows = targetRoles.map((role) => ({
      "Role": role.roleName || role.name || "-",
      "Description": role.description || "-",
      "Permissions": typeof role.permissions === "string" ? role.permissions : (Array.isArray(role.permissions) ? role.permissions.join(", ") : "-"),
      "Users": role.usersCount != null ? role.usersCount : 0,
      "Status": role.status || "ACTIVE",
    }));

    try {
      await downloadBackendExcel({
        url: `${config.baseURL}/excel/download`,
        method: "POST",
        data: {
          table: "roles",
          organizationId: parsedUserState?.user?.organizationId || parsedUserState?.organization?.id,
          format: "xlsx",
          includeDetails: true,
          rows: exportRows,
        },
        defaultFileName: downloadFileName,
      });
    } catch (err) {
      console.warn("Backend POST /excel/download roles failed, trying table=role:", err);
      try {
        await downloadBackendExcel({
          url: `${config.baseURL}/excel/download`,
          method: "POST",
          data: {
            table: "role",
            organizationId: parsedUserState?.user?.organizationId || parsedUserState?.organization?.id,
            format: "xlsx",
            includeDetails: true,
            rows: exportRows,
          },
          defaultFileName: downloadFileName,
        });
      } catch (err2) {
        console.warn("Backend POST /excel/download role failed, trying GET fallback:", err2);
        try {
          await downloadBackendExcel({
            url: `${config.baseURL}/excel/download`,
            method: "GET",
            params: {
              table: "role",
              organizationId: parsedUserState?.user?.organizationId || parsedUserState?.organization?.id,
              format: "xlsx",
              includeDetails: true,
              ...(selectedRoleIds && selectedRoleIds.length > 0
                ? { roleIds: selectedRoleIds.join(","), ids: selectedRoleIds.join(",") }
                : {}),
            },
            defaultFileName: downloadFileName,
          });
        } catch (err3) {
          console.warn("Backend GET /excel/download table=role failed, trying /excel/download/role:", err3);
          try {
            await downloadBackendExcel({
              url: `${config.baseURL}/excel/download/role`,
              method: "GET",
              params: {
                organizationId: parsedUserState?.user?.organizationId || parsedUserState?.organization?.id,
                format: "xlsx",
                includeDetails: true,
              },
              defaultFileName: downloadFileName,
            });
          } catch (fallbackErr) {
            console.warn("Backend Excel export for roles failed, using client XLSX fallback:", fallbackErr);
            const detailedHeaders = [
              { key: "Role", label: "Role" },
              { key: "Description", label: "Description" },
              { key: "Permissions", label: "Permissions" },
              { key: "Users", label: "Users" },
              { key: "Status", label: "Status" },
            ];
            exportTableToCSV(roleExportFileName, detailedHeaders, exportRows);
          }
        }
      }
    }
  };

  return (
    <div className="role-main-container">
      <SubTitleNew
        title={t("Roles")}
        path={`${orgName || "organization"} > Users`}
        add={canCreateRole ? "Create Role" : null}
        onAddClick={canCreateRole ? handleCreateRole : null}
        showDashboardControls={false}
        breadcrumbHandlers={{ Users: usersListPath }}
      />

      <Box className="users-page-content">
        {showCreateRole && canCreateRole ? (
          <RoleAdd
            embedded
            createRole={createRole}
            onCancel={() => setShowCreateRole(false)}
            onComplete={(saved) => handleRoleFormComplete(saved, { created: true })}
          />
        ) : null}

        <div className="stats-cards-container">
          <StatsCard
            title={t("Roles")}
            value={roleList.length}
            icon={AdminPanelSettingsOutlinedIcon}
            tone="blue"
          />
          {/* <StatsCard
            title={t("Active Roles")}
            value={activeRolesCount}
            icon={PersonOutlineIcon}
            tone="blue"
          /> */}
        </div>

        <div className={`users-table-wrapper${hasMoreRoles ? " has-more" : ""}`}>
          <Suspense fallback={<CircularProgress />}>
            <DynamicTable
              title="All Roles"
              className="users-dynamic-table"
              showTotalCount={false}
              headers={displayHeaders}
              data={visibleRoles}
              downloadData={
                selectedRoleIds && selectedRoleIds.length > 0
                  ? filteredRoles.filter((r) => selectedRoleIds.some((id) => String(id) === String(r.id)))
                  : filteredRoles
              }
              downloadFileName={roleExportFileName}
              onDownload={handleDownloadRoles}
              selectable={true}
              selectedRowKeys={selectedRoleIds}
              onSelectionChange={setSelectedRoleIds}
              actions={displayActions}
              loading={isLoading}
              searchPlaceholder="Search roles"
              searchValue={searchText}
              onSearchChange={handleSearchChange}
              // filters={[
              //   {
              //     key: "status",
              //     label: "Status",
              //     value: statusFilter,
              //     onChange: (value) => {
              //       setStatusFilter(value);
              //       setExpandedRoleId(null);
              //       setVisibleRoleCount(10);
              //     },
              //     options: [
              //       { value: "", label: "Status" },
              //       { value: "ACTIVE", label: "Active" },
              //       { value: "INACTIVE", label: "Inactive" },
              //     ],
              //   },
              // ]}
              pagination={{ enabled: false, pageSize: 10, itemLabel: "roles" }}
              minWidth={860}
              onRowClick={
                canUpdateRole
                  ? (row) => toggleExpanded(row.id, "edit")
                  : canViewRole
                    ? (row) => toggleExpanded(row.id, "view")
                    : undefined
              }
              renderExpandedRow={(row) =>
                String(expandedRoleId) === String(row.id) && expandedRole ? (
                  <div className="users-expanded-form">
                    <RoleAdd
                      key={`${row.id}-${expandedMode}`}
                      embedded
                      readOnly={expandedMode === "view"}
                      roleToEdit={expandedRole}
                      updateRole={updateRole}
                      getAllTheAvailableFeatures={getAllTheAvailableFeatures}
                      assignFeatureToTheRole={assignFeatureToTheRole}
                      isAssigning={isAssigning}
                      onCancel={closeForms}
                      onComplete={() => handleRoleFormComplete(expandedRole)}
                    />
                  </div>
                ) : null
              }
            />
          </Suspense>
          {hasMoreRoles && (
            <div className="users-show-more-footer">
              <span>
                {t("Showing")} {visibleRoles.length} {t("of")} {filteredRoles.length} {t("roles")}
              </span>
              <button
                type="button"
                onClick={() => setVisibleRoleCount((count) => Math.min(count + 10, filteredRoles.length))}
              >
                {t("Show more")}
              </button>
            </div>
          )}
        </div>
      </Box>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setRoleToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Role"
        message={`Are you sure you want to delete ${roleToDelete?.name || "this role"
          }? This action cannot be undone.`}
      />
    </div>
  );
}
